import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeSlash, Copy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

function CopyField({ label, value, testId }) {
  const copy = async () => {
    if (!value) return;
    const ok = await copyToClipboard(String(value));
    if (ok) toast.success(`${label} copied`);
    else toast.error(`Couldn't copy ${label}. Please copy it manually.`);
  };
  return (
    <p className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-igc-muted">{label}:</span> {value || "—"}
      {value && (
        <button type="button" data-testid={`${testId}-copy-button`} onClick={copy} title={`Copies this client's ${label} to your clipboard so you can paste it somewhere else (like an email or a form) without retyping it.`} className="text-igc-muted hover:text-igc-goldText transition-colors">
          <Copy size={12} weight="duotone" />
        </button>
      )}
    </p>
  );
}

export function AdminClientProfileModal({ clientId, open, onOpenChange }) {
  const [profile, setProfile] = useState(null);
  const [revealed, setRevealed] = useState(null);

  useEffect(() => {
    if (open && clientId) {
      setRevealed(null);
      api.get(`/admin/clients/${clientId}/profile`).then(({ data }) => setProfile(data));
    }
  }, [open, clientId]);

  const toggleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    try {
      const { data } = await api.get(`/admin/clients/${clientId}/profile/reveal`);
      setRevealed(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyAll = async () => {
    const lines = [
      `Legal First Name: ${profile.first_name}`,
      `Legal Last Name: ${profile.last_name}`,
      `Email: ${profile.email}`,
      `Phone: ${profile.phone}`,
      `Personal Address: ${profile.mailing_address}`,
      `Business Name: ${profile.business_name || "—"}`,
      `Business Address: ${profile.business_address || "—"}`,
      `Bank: ${profile.payment_bank_name || "—"}`,
      `Account Holder: ${profile.payment_account_holder_name || "—"}`,
      `Account #: ${revealed ? revealed.payment_account_number : profile.payment_account_number_masked}`,
      `Routing #: ${revealed ? revealed.payment_routing_number : profile.payment_routing_number_masked}`,
      `Account Type: ${profile.account_type || "—"}`,
    ];
    if (profile.legal_name !== undefined) {
      lines.push(`Legal Name (1099): ${profile.legal_name || "—"}`);
      lines.push(`${profile.tax_id_type || "Tax ID"}: ${revealed ? revealed.tax_id : profile.tax_id_masked}`);
      lines.push(`Tax Mailing Address: ${profile.tax_mailing_address || "—"}`);
    }
    const ok = await copyToClipboard(lines.join("\n"));
    if (ok) toast.success("All fields copied");
    else toast.error("Couldn't copy to clipboard. Please copy fields individually.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream max-w-xl max-h-[85vh] overflow-y-auto" data-testid="admin-client-profile-modal">
        <DialogHeader><DialogTitle className="font-serif" title="Shows every detail this client entered during profile setup — legal name, contact info, address, and their banking/tax info for payouts.">Client Profile — {clientId}</DialogTitle></DialogHeader>
        {!profile ? (
          <p className="text-igc-muted text-sm">Loading...</p>
        ) : !profile.exists ? (
          <p className="text-igc-muted text-sm" data-testid="no-profile-message">This client hasn't completed their profile setup yet.</p>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="flex justify-end">
              <Button data-testid="admin-copy-all-button" onClick={copyAll} title="Copies every field on this profile at once (name, contact info, addresses, and banking/tax info) as one block of text, ready to paste into an email or document." className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70 text-xs flex items-center gap-1.5">
                <Copy size={13} weight="duotone" /> Copy All
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <CopyField label="Legal First Name" value={profile.first_name} testId="admin-first-name" />
              <CopyField label="Legal Last Name" value={profile.last_name} testId="admin-last-name" />
              <CopyField label="Email" value={profile.email} testId="admin-email" />
              <CopyField label="Phone" value={profile.phone} testId="admin-phone" />
              <div className="col-span-2"><CopyField label="Personal Address" value={profile.mailing_address} testId="admin-mailing-address" /></div>
              <CopyField label="Business Name" value={profile.business_name} testId="admin-business-name" />
              <div className="col-span-2"><CopyField label="Business Address" value={profile.business_address} testId="admin-business-address" /></div>
            </div>
            <div className="border-t border-igc-gold/10 pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-igc-goldText text-xs uppercase tracking-widest">Payment & Tax Information</p>
                <button type="button" data-testid="admin-reveal-sensitive-info-button" onClick={toggleReveal} title="Bank account and tax ID numbers are masked by default for security. Click this to temporarily reveal the full numbers on screen — click again (Hide) to mask them back." className="flex items-center gap-1 text-xs text-igc-muted hover:text-igc-goldText">
                  {revealed ? <EyeSlash size={14} /> : <Eye size={14} />} {revealed ? "Hide" : "Reveal"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <CopyField label="Bank" value={profile.payment_bank_name} testId="admin-bank-name" />
                <CopyField label="Account Holder" value={profile.payment_account_holder_name} testId="admin-account-holder" />
                <CopyField label="Account #" value={revealed ? revealed.payment_account_number : profile.payment_account_number_masked} testId="admin-account-number-display" />
                <CopyField label="Routing #" value={revealed ? revealed.payment_routing_number : profile.payment_routing_number_masked} testId="admin-routing-number-display" />
                <CopyField label="Account Type" value={profile.account_type} testId="admin-account-type" />
                {profile.legal_name !== undefined && (
                  <>
                    <CopyField label="Legal Name (1099)" value={profile.legal_name} testId="admin-legal-name" />
                    <CopyField label={profile.tax_id_type || "Tax ID"} value={revealed ? revealed.tax_id : profile.tax_id_masked} testId="admin-tax-id-display" />
                    <div className="col-span-2"><CopyField label="Tax Mailing Address" value={profile.tax_mailing_address} testId="admin-tax-mailing-address" /></div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
